import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';
import SettingsCard from 'components/kea/SettingsCard/SettingsCard';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const HISTORY_LIMIT = 10;

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(1),
  },
  headCell: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  emptyCell: {
    color: theme.palette.text.secondary,
  },
  progressCell: {
    whiteSpace: 'nowrap',
  },
  dateCell: {
    whiteSpace: 'nowrap',
  },
}));

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function historyStatusColor(rawStatus) {
  const s = String(rawStatus || '').toLowerCase();
  if (s === 'completed' || s === 'done') return 'primary';
  if (s === 'running' || s === 'queued') return 'secondary';
  return 'default';
}

export default function IndexingHistorySection({ indexingApi, testIdPrefix = 'data' }) {
  const t = useLocalizedMessage();
  const classes = useStyles();
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!indexingApi || typeof indexingApi.getHistory !== 'function') {
      setHistory([]);
      setHistoryLoading(false);
      setHistoryError(t('search.data.indexing.historyBridgeOnly'));
      return;
    }
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { history: list } = await indexingApi.getHistory(HISTORY_LIMIT);
      setHistory(list);
    } catch (err) {
      setHistoryError(err.message || t('search.data.indexing.historyLoadFailed'));
    } finally {
      setHistoryLoading(false);
    }
  }, [indexingApi, t]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <SettingsCard>
      <Box className={classes.header}>
        <Typography variant="h6"><IntlMessages id="ui.dataSettings.indexingHistorySection.recentJobs" /></Typography>
        <Button
          size="small"
          onClick={fetchHistory}
          disabled={historyLoading}
          startIcon={historyLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
          data-testid={`${testIdPrefix}-history-refresh-btn`}
        >
          <IntlMessages id="ui.dataSettings.indexingHistorySection.refresh" />
        </Button>
      </Box>

      {historyError && (
        <Alert severity="error" data-testid={`${testIdPrefix}-history-error`}>
          {historyError}
        </Alert>
      )}

      {!historyError && (
        <TableContainer
          className={classes.tableContainer}
          data-testid={`${testIdPrefix}-indexing-history`}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.job" /></TableCell>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.status" /></TableCell>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.progress" /></TableCell>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.indexed" /></TableCell>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.started" /></TableCell>
                <TableCell className={classes.headCell}><IntlMessages id="ui.dataSettings.indexingHistorySection.finished" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {!historyLoading && history.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className={classes.emptyCell}
                  data-testid={`${testIdPrefix}-history-empty`}
                >
                  <IntlMessages id="ui.dataSettings.indexingHistorySection.noIndexingJobsYet" />
                </TableCell>
              </TableRow>
            )}
            {history.map((job) => (
              <TableRow key={job.job_id}>
                <TableCell>{job.job_id}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={String(job.status || 'unknown').toUpperCase()}
                    color={historyStatusColor(job.status)}
                  />
                </TableCell>
                <TableCell className={classes.progressCell}>
                  {typeof job.progress === 'number' ? `${job.progress}%` : '—'}
                </TableCell>
                <TableCell className={classes.progressCell}>
                  {(typeof job.indexed_count === 'number' && typeof job.total_count === 'number')
                    ? `${job.indexed_count} / ${job.total_count}`
                    : '—'}
                </TableCell>
                <TableCell className={classes.dateCell}>{formatDate(job.started_at || job.created_at)}</TableCell>
                <TableCell className={classes.dateCell}>{formatDate(job.finished_at)}</TableCell>
              </TableRow>
            ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SettingsCard>
  );
}

IndexingHistorySection.propTypes = {
  indexingApi: PropTypes.shape({
    getHistory: PropTypes.func.isRequired,
  }),
  testIdPrefix: PropTypes.string,
};

IndexingHistorySection.defaultProps = {
  indexingApi: null,
  testIdPrefix: 'data',
};
